import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Medal, Target, Chats } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { getAdvisorLeaderboard } from "../lib/api";

/**
 * Reputation Hall of Fame — top community advisors.
 *
 * Ranking is driven by `users.reputation` (sorted desc). Each row shows
 * cast/correct counts for both market advice and prediction votes with
 * a blended accuracy percentage when enough activity is resolved.
 */
export default function Leaderboard() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [limit, setLimit] = useState(20);

    useEffect(() => {
        setLoading(true);
        getAdvisorLeaderboard(limit)
            .then((d) => setItems(d.items || []))
            .finally(() => setLoading(false));
    }, [limit]);

    return (
        <AppShell>
            <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="leaderboard-page">
                <header className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display font-black text-4xl sm:text-5xl bg-gradient-to-r from-fa-cream via-white to-fa-mint bg-clip-text text-transparent">
                            Reputation Hall of Fame
                        </h1>
                        <p className="font-mono text-xs uppercase tracking-widest text-white/50 mt-1">
                            Top community advisors · ranked by reputation
                        </p>
                    </div>
                    <select
                        value={limit}
                        onChange={(e) => setLimit(Number(e.target.value))}
                        className="bg-black border border-white/15 text-white font-mono text-xs uppercase tracking-widest px-3 py-2"
                        data-testid="leaderboard-limit"
                    >
                        <option value={10}>Top 10</option>
                        <option value={20}>Top 20</option>
                        <option value={50}>Top 50</option>
                        <option value={100}>Top 100</option>
                    </select>
                </header>

                {loading ? (
                    <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-white/40">
                        Loading leaderboard…
                    </div>
                ) : items.length === 0 ? (
                    <div className="py-20 text-center font-mono text-xs uppercase tracking-widest text-white/40">
                        No community activity yet
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="leaderboard-list">
                        {items.map((it) => (
                            <LeaderRow key={it.user_id} item={it} />
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
}

function LeaderRow({ item }) {
    const rankIcon = _rankIcon(item.rank);
    return (
        <Link
            to={`/u/${item.user_id}`}
            className="flex items-center gap-4 border border-white/10 bg-[#0d0d0d] hover:border-fa-cream/40 hover:bg-fa-violet/10 transition-colors px-4 py-3 group"
            data-testid={`leaderboard-row-${item.rank}`}
        >
            <div className="w-10 text-center flex-shrink-0">
                {rankIcon ? (
                    rankIcon
                ) : (
                    <span className="font-display font-black text-2xl text-white/40">
                        {item.rank}
                    </span>
                )}
            </div>

            <div className="w-10 h-10 flex-shrink-0 border border-fa-violet/30 bg-fa-violet/10 flex items-center justify-center overflow-hidden">
                {item.picture ? (
                    <img src={item.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                    <span className="font-display font-black text-base text-fa-cream">
                        {(item.name || "?").charAt(0).toUpperCase()}
                    </span>
                )}
            </div>

            <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm text-white truncate group-hover:text-fa-cream transition-colors">
                    {item.name}
                </div>
                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
                    <span className="flex items-center gap-1">
                        <Target size={10} weight="bold" />
                        {item.advice_casts + item.vote_casts} casts
                    </span>
                    <span className="flex items-center gap-1">
                        <Chats size={10} weight="bold" />
                        {item.advice_correct + item.vote_correct} correct
                    </span>
                    {(item.advice_casts + item.vote_casts) > 0 && (
                        <span
                            className={
                                item.accuracy_pct >= 60 ? "text-fa-mint" :
                                item.accuracy_pct >= 40 ? "text-fa-cream" :
                                "text-white/40"
                            }
                        >
                            {item.accuracy_pct}% accuracy
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                <Trophy size={14} weight="fill" className="text-fa-cream" />
                <span
                    className="font-display font-black text-xl text-fa-cream"
                    data-testid={`leaderboard-rep-${item.rank}`}
                >
                    {item.reputation}
                </span>
            </div>
        </Link>
    );
}

function _rankIcon(rank) {
    if (rank === 1) return <Crown size={24} weight="fill" className="text-yellow-400 mx-auto" />;
    if (rank === 2) return <Medal size={22} weight="fill" className="text-slate-300 mx-auto" />;
    if (rank === 3) return <Medal size={20} weight="fill" className="text-amber-700 mx-auto" />;
    return null;
}
